import React, { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { PhoneInput } from '../ui/PhoneInput';
import { AddressBlock, AddressValue, EMPTY_ADDRESS, isAddressValid } from '../ui/AddressBlock';
import { DemoFillBanner } from '../ui/DemoFillBanner';
import { SAMPLE_BUSINESS_INFO } from '../../constants/sampleData';
import { cleanPhoneMY } from '../../constants/validators';
interface BusinessInfoJourneyProps {
  onComplete: (data: any) => void;
  onExit: () => void;
  initialData?: any;
  /** Phone number from signup, used to pre-fill the customer support phone. */
  prefillSupportPhone?: string;
}
type BusinessType = '' | 'individual' | 'sole_proprietor' | 'partnership' | 'corporation' | 'non_profit';
const BUSINESS_TYPE_OPTIONS = [{
  value: 'individual',
  label: 'Individual'
}, {
  value: 'sole_proprietor',
  label: 'Sole Proprietorship'
}, {
  value: 'partnership',
  label: 'Partnership'
}, {
  value: 'corporation',
  label: 'Corporation (Sdn Bhd / Bhd)'
}, {
  value: 'non_profit',
  label: 'Non-profit'
}];
const PRODUCT_CATEGORIES = [{
  value: 'software',
  label: 'Software'
}, {
  value: 'clothing',
  label: 'Clothing and accessories'
}, {
  value: 'consulting',
  label: 'Consulting services'
}, {
  value: 'retail',
  label: 'Retail'
}, {
  value: 'digital',
  label: 'Digital products'
}, {
  value: 'food',
  label: 'Food and drink'
}, {
  value: 'professional',
  label: 'Professional services'
}];
export function BusinessInfoJourney({
  onComplete,
  onExit,
  initialData,
  prefillSupportPhone = ''
}: BusinessInfoJourneyProps) {
  // Basics
  const [businessType, setBusinessType] = useState<BusinessType>(initialData?.businessType ?? '');
  const [legalName, setLegalName] = useState(initialData?.legalName ?? '');
  const [dba, setDba] = useState(initialData?.dba ?? '');
  const [regNumber, setRegNumber] = useState(initialData?.regNumber ?? '');
  // Address
  const [address, setAddress] = useState<AddressValue>(initialData?.address ?? EMPTY_ADDRESS);
  // Product & Services
  const [productCategory, setProductCategory] = useState(initialData?.productCategory ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  // Tax (conditional)
  const [tin, setTin] = useState(initialData?.tin ?? '');
  const [sstRegistered, setSstRegistered] = useState<'' | 'yes' | 'no'>(initialData?.sstRegistered ?? '');
  const [sstId, setSstId] = useState(initialData?.sstId ?? '');
  // Public details
  const [statementDescriptor, setStatementDescriptor] = useState(initialData?.statementDescriptor ?? '');
  // If we already have data, mark descriptor as touched so auto-prefill
  // doesn't overwrite the user's saved value on re-entry.
  const [statementDescriptorTouched, setStatementDescriptorTouched] = useState(Boolean(initialData?.statementDescriptor));
  const [shortDescriptor, setShortDescriptor] = useState(initialData?.shortDescriptor ?? '');
  // Always normalize any seeded phone value (strip non-digits + leading zero)
  // so prefilled values follow the same rule as keyed-in input.
  const [supportPhone, setSupportPhone] = useState(cleanPhoneMY(initialData?.supportPhone ?? prefillSupportPhone ?? ''));
  // Step management
  type Step = 'basics' | 'products' | 'tax' | 'public';
  const [currentStep, setCurrentStep] = useState<Step>('basics');
  // Sanitize for card-network statement descriptors:
  // strip <, >, \, * and other invalid chars; uppercase; truncate to 22.
  const formatDescriptor = (raw: string) => raw.replace(/[<>\\*]/g, '').replace(/[^A-Za-z0-9 &.,\-']/g, '').replace(/\s+/g, ' ').toUpperCase().slice(0, 22);
  // Auto-populate Statement Descriptor from DBA (primary) or Legal Name (fallback)
  // until the user manually edits it.
  useEffect(() => {
    if (statementDescriptorTouched) return;
    const source = dba.trim() ? dba : legalName;
    setStatementDescriptor(formatDescriptor(source));
  }, [dba, legalName, statementDescriptorTouched]);
  const isCorporation = businessType === 'corporation';
  // Tax Information shows for all business types EXCEPT Individual (and the
  // empty placeholder state). SST radio + ID logic now applies to all of these.
  const showTaxGroup = businessType !== '' && businessType !== 'individual';
  // Step validation
  const isBasicsValid = businessType !== '' && legalName.trim().length > 0 && regNumber.trim().length > 0 && isAddressValid(address);
  const isProductsValid = productCategory !== '' && description.trim().length > 10;
  const isTaxValid = !showTaxGroup ? true : tin.trim().length > 0 && sstRegistered !== '' && (sstRegistered === 'no' || sstId.trim().length > 0);
  const isPublicValid = statementDescriptor.trim().length > 0 && statementDescriptor.length <= 22 && shortDescriptor.length <= 10 && supportPhone.trim().length >= 9;
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 'basics':
        return isBasicsValid;
      case 'products':
        return isProductsValid;
      case 'tax':
        return isTaxValid;
      case 'public':
        return isPublicValid;
      default:
        return false;
    }
  };
  const handleNext = () => {
    if (!isCurrentStepValid()) return;
    if (currentStep === 'basics') {
      setCurrentStep('products');
    } else if (currentStep === 'products') {
      setCurrentStep(showTaxGroup ? 'tax' : 'public');
    } else if (currentStep === 'tax') {
      setCurrentStep('public');
    } else if (currentStep === 'public') {
      handleSubmit();
    }
  };
  const handleBack = () => {
    if (currentStep === 'basics') {
      onExit();
    } else if (currentStep === 'products') {
      setCurrentStep('basics');
    } else if (currentStep === 'tax') {
      setCurrentStep('products');
    } else if (currentStep === 'public') {
      setCurrentStep(showTaxGroup ? 'tax' : 'products');
    }
  };
  const handleSubmit = () => {
    if (!isCurrentStepValid()) return;
    onComplete({
      businessType,
      legalName,
      dba,
      regNumber,
      address,
      productCategory,
      description,
      tin: showTaxGroup ? tin : '',
      sstRegistered: isCorporation ? sstRegistered : '',
      sstId: isCorporation && sstRegistered === 'yes' ? sstId : '',
      statementDescriptor,
      shortDescriptor,
      supportPhone
    });
  };
  return <div className="flex flex-col h-full max-w-xl mx-auto pt-2 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-120">
      <button onClick={handleBack} className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-6 self-start" data-id="element-121">
        <ChevronLeft className="w-4 h-4 mr-1" data-id="element-122" />
        {currentStep === 'basics' ? 'Back to Dashboard' : 'Back'}
      </button>

      {/* Header */}
      <div className="mb-6" data-id="element-123">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-124">
          {currentStep === 'basics' && 'Tell us about your business'}
          {currentStep === 'products' && 'Products & Services'}
          {currentStep === 'tax' && 'Tax Information'}
          {currentStep === 'public' && 'Public Details'}
        </h1>
        <p className="text-gray-600" data-id="element-125">
          {currentStep === 'basics' && "The legal structure you choose determines what we'll need next."}
          {currentStep === 'products' && 'Help us understand what you sell.'}
          {currentStep === 'tax' && 'Required for regulatory compliance.'}
          {currentStep === 'public' && 'How your business appears to customers.'}
        </p>
      </div>

      <DemoFillBanner className="mb-8" onFill={() => {
      setBusinessType(SAMPLE_BUSINESS_INFO.businessType);
      setLegalName(SAMPLE_BUSINESS_INFO.legalName);
      setDba(SAMPLE_BUSINESS_INFO.dba);
      setRegNumber(SAMPLE_BUSINESS_INFO.regNumber);
      setAddress(SAMPLE_BUSINESS_INFO.address);
      setProductCategory(SAMPLE_BUSINESS_INFO.productCategory);
      setDescription(SAMPLE_BUSINESS_INFO.description);
      setTin(SAMPLE_BUSINESS_INFO.tin);
      setSstRegistered(SAMPLE_BUSINESS_INFO.sstRegistered);
      setSstId(SAMPLE_BUSINESS_INFO.sstId);
      setStatementDescriptor(SAMPLE_BUSINESS_INFO.statementDescriptor);
      setStatementDescriptorTouched(true);
      setShortDescriptor(SAMPLE_BUSINESS_INFO.shortDescriptor);
      setSupportPhone(SAMPLE_BUSINESS_INFO.supportPhone);
    }} data-id="element-126" />

      {/* Form */}
      <div className="space-y-10 flex-1" data-id="element-127">
        {currentStep === 'basics' && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-128">
            {/* Business Basics */}
            <FormGroup title="Business Basics" data-id="element-129">
              <Select label="Business Type" placeholder="Select your business structure" options={BUSINESS_TYPE_OPTIONS} value={businessType} onChange={e => setBusinessType(e.target.value as BusinessType)} required data-id="element-130" />
              <Input label="Legal Business Name" placeholder="Must match official registration" value={legalName} onChange={e => setLegalName(e.target.value)} required data-id="element-131" />
              <div data-id="element-132">
                <Input label="Doing Business As (Optional)" placeholder="e.g. Acme Mart" value={dba} onChange={e => setDba(e.target.value)} data-id="element-133" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-134">
                  The operating name of your company, if it's different from the
                  legal name.
                </p>
              </div>
              <Input label="Business Registration Number" placeholder="e.g. 202101012345 (SSM)" value={regNumber} onChange={e => setRegNumber(e.target.value)} required data-id="element-135" />
            </FormGroup>

            {/* Business Address */}
            <FormGroup title="Business Address" subtitle="Use the registered business address from SSM." data-id="element-136">
              <AddressBlock value={address} onChange={setAddress} required data-id="element-137" />
            </FormGroup>
          </div>}

        {currentStep === 'products' && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-138">
            {/* Product & Services */}
            <FormGroup title="Product & Services" data-id="element-139">
              <Select label="Product Category" placeholder="Select the closest match" options={PRODUCT_CATEGORIES} value={productCategory} onChange={e => setProductCategory(e.target.value)} required data-id="element-140" />
              <Textarea id="bizDescription" label="Description" rows={4} value={description} onChange={e => setDescription(e.target.value)} placeholder="Briefly describe the products or services you offer." required data-id="element-141" />
            </FormGroup>
          </div>}

        {currentStep === 'tax' && showTaxGroup && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-142">
            {/* Conditional Tax Info */}
            <FormGroup title="Tax Information" subtitle={isCorporation ? 'Required for Corporation (Sdn Bhd / Bhd) entities.' : 'Required for Sole Proprietors.'} data-id="element-143">
              <Input label={isCorporation ? 'Corporate Tax Identification Number (MY TIN)' : 'Tax Identification Number (MY TIN)'} placeholder={isCorporation ? 'e.g. C12345678901' : 'e.g. SG12345678901'} value={tin} onChange={e => setTin(e.target.value)} required data-id="element-144" />

              <fieldset data-id="element-145">
                <legend className="block text-sm font-medium text-gray-900 mb-2" data-id="element-146">
                  Is your company SST registered?
                  <span className="text-red-600 ml-0.5" aria-hidden="true" data-id="element-147">
                    *
                  </span>
                  <span className="sr-only" data-id="element-148"> (required)</span>
                </legend>
                <div className="grid grid-cols-2 gap-3" data-id="element-149">
                  <RadioCard label="Yes" selected={sstRegistered === 'yes'} onClick={() => setSstRegistered('yes')} data-id="element-150" />
                  <RadioCard label="No" selected={sstRegistered === 'no'} onClick={() => {
                setSstRegistered('no');
                setSstId('');
              }} data-id="element-151" />
                </div>
              </fieldset>

              {sstRegistered === 'yes' && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-152">
                  <Input label="SST ID" placeholder="e.g. W10-1234-12345678" value={sstId} onChange={e => setSstId(e.target.value)} required data-id="element-153" />
                </div>}
            </FormGroup>
          </div>}

        {currentStep === 'public' && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-154">
            {/* Public Details */}
            <FormGroup title="Public details for customers" subtitle="These details may appear in payment statements, invoices, and receipts." data-id="element-155">
              <div data-id="element-156">
                <CountedInput label="Statement Descriptor" placeholder="e.g. ACMEMART KL" value={statementDescriptor} onChange={v => {
              setStatementDescriptorTouched(true);
              setStatementDescriptor(formatDescriptor(v));
            }} max={22} required data-id="element-157" />
                <p className="mt-1 text-xs text-gray-600" data-id="element-158">
                  How your business appears on customer card statements.
                  Auto-filled from your business name — edit if needed.
                </p>
              </div>
              <CountedInput label="Shortened Descriptor (Optional)" placeholder="e.g. ACMEMART" value={shortDescriptor} onChange={v => setShortDescriptor(v.slice(0, 10))} max={10} data-id="element-159" />
              <PhoneInput label="Customer Support Phone Number" value={supportPhone} onChange={setSupportPhone} helperText="Pre-filled from your login details. You can change this if your business has a dedicated support line." required data-id="element-160" />
            </FormGroup>
          </div>}
      </div>

      {/* Sticky Footer */}
      <div className="sticky bottom-0 left-0 right-0 -mx-2 mt-12 bg-white/95 backdrop-blur-sm border-t border-gray-100" data-id="element-161">
        <div className="flex items-center justify-between py-4 px-2" data-id="element-162">
          <Button variant="outline" onClick={handleBack} data-id="element-163">
            Back
          </Button>
          <Button onClick={handleNext} disabled={!isCurrentStepValid()} data-id="element-164">
            {currentStep === 'public' ? 'Save & Continue' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>;
}
function FormGroup({
  title,
  subtitle,
  children
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-165">
      <div className="mb-4" data-id="element-166">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide" data-id="element-167">
          {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1" data-id="element-168">{subtitle}</p>}
      </div>
      <div className="space-y-5" data-id="element-169">{children}</div>
    </div>;
}
function CountedInput({
  label,
  placeholder,
  value,
  onChange,
  max,
  required
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
  required?: boolean;
}) {
  const isOver = value.length > max;
  const isFull = value.length === max;
  return <Input label={label} labelAside={<span aria-live="polite" className={isOver ? 'text-red-700 font-medium' : isFull ? 'text-gray-900 font-medium' : 'text-gray-600'} data-id="element-171">
          {value.length} / {max}
        </span>} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value.slice(0, max))} maxLength={max} required={required} data-id="element-170" />;
}
function RadioCard({
  label,
  selected,
  onClick
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className={`relative text-left p-4 rounded-lg border-2 transition-all ${selected ? 'border-[#FFCC00] bg-[#FFCC00]/10' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-172">
      <div className="flex items-center gap-3" data-id="element-173">
        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-[#FFCC00]' : 'border-gray-300'}`} data-id="element-174">
          {selected && <span className="w-2 h-2 rounded-full bg-[#FFCC00]" data-id="element-175" />}
        </span>
        <span className="font-semibold text-sm text-gray-900" data-id="element-176">{label}</span>
      </div>
    </button>;
}